import { Editor, TLShape } from "tldraw";
import { InkCropsPart } from "../../shared/types/PromptPart";
import { inkCropsEnabled } from "../../hooks/useRuntimeFlags";
import { AgentRequest } from "../../shared/types/AgentRequest";
import { AgentHelpers } from "../AgentHelpers";
import { computeInkCrops } from "../ink/inkCrops";
import { PromptPartUtil, registerPromptPartUtil } from "./PromptPartUtil";

/**
 * One image per handwriting cluster. Same grouping as the screenshot badges and
 * the `inkClusters` digest, so `ink_9` means the same writing in all three.
 */
export const InkCropsPartUtil = registerPromptPartUtil(
  class InkCropsPartUtil extends PromptPartUtil<InkCropsPart> {
    static override type = "inkCrops" as const;

    override async getPart(
      request: AgentRequest,
      helpers: AgentHelpers,
    ): Promise<InkCropsPart> {
      const { editor } = this;
      // Checked before the clusters: the rasterizing is what this flag is for.
      if (!inkCropsEnabled()) return { type: "inkCrops", crops: [] };

      const clusters = helpers.getStudentInkClusters(request.bounds);
      if (clusters.length === 0) return { type: "inkCrops", crops: [] };

      const crops = await computeInkCrops(editor, this.agent, clusters, {
        renderBatch: (shapes, opts) => renderCrop(editor, shapes, opts),
        // Yield so audio callbacks can fire through a burst of new clusters.
        onCropBoundary: () =>
          new Promise<void>((resolve) => setTimeout(resolve, 0)),
      });
      return { type: "inkCrops", crops };
    }
  },
);

/**
 * A crop is a bonus copy of writing the page screenshot already carries, so a
 * failed render must not fail the turn. No taint retry — crops hold only `draw`
 * shapes, so there is no cross-origin image to taint the canvas.
 */
async function renderCrop(
  editor: Editor,
  shapes: TLShape[],
  opts: Parameters<Editor["toImage"]>[1],
): Promise<{ blob: Blob } | null> {
  try {
    return await editor.toImage(shapes, opts);
  } catch (e) {
    console.warn("[InkCropsPartUtil] crop render failed", e);
    return null;
  }
}
